import React, { useMemo } from 'react';

import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom, TickRendererProps } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { curveCatmullRom } from 'd3-shape';

type AreaDataPoint = {
  yFloor: number;
  yCeiling: number;
  x: number;
};

type TrendLineDataPoint = {
  x: number;
  y: number;
};

const areaData: AreaDataPoint[] = [
  { yFloor: 3.0, yCeiling: 3.0, x: 7 },
  { yFloor: 3.02, yCeiling: 3.23, x: 8 },
  { yFloor: 3.04, yCeiling: 3.68, x: 9 },
  { yFloor: 3.06, yCeiling: 4.24, x: 10 },
  { yFloor: 3.08, yCeiling: 4.91, x: 11 },
  { yFloor: 3.1, yCeiling: 5.67, x: 12 },
  { yFloor: 3.12, yCeiling: 6.5, x: 13 },
  { yFloor: 3.14, yCeiling: 7.43, x: 14 },
  { yFloor: 3.16, yCeiling: 8.41, x: 15 },
  { yFloor: 3.18, yCeiling: 9.45, x: 16 },
  { yFloor: 3.22, yCeiling: 10.55, x: 17 },
  { yFloor: 3.24, yCeiling: 11.72, x: 18 },
  { yFloor: 3.26, yCeiling: 12.93, x: 19 },
  { yFloor: 3.28, yCeiling: 14.2, x: 20 },
];

const trendLineData: TrendLineDataPoint[] = [
  { x: 7, y: 3.0 },
  { x: 8, y: 3.03 },
  { x: 9, y: 3.11 },
  { x: 10, y: 3.24 },
  { x: 11, y: 3.43 },
  { x: 12, y: 3.67 },
  { x: 13, y: 3.96 },
  { x: 14, y: 4.31 },
  { x: 15, y: 4.71 },
  { x: 16, y: 5.16 },
  { x: 17, y: 5.67 },
  { x: 18, y: 6.23 },
  { x: 19, y: 6.84 },
  { x: 20, y: 7.5 },
];

type ChartVariant = 'loading' | 'zero-state';

type Props = {
  height: number;
  width: number;
  variant: ChartVariant;
};

const defaultMargin = { top: 10, bottom: 16 };

const xAccessor = (d: AreaDataPoint) => d.x;
const yFloorAccessor = (d: AreaDataPoint) => d.yFloor;
const yCeilingAccessor = (d: AreaDataPoint) => d.yCeiling;

const CustomTickBottom = ({ x, transform }: TickRendererProps) => {
  const height = 8;
  const width = 24;

  return (
    <Group transform={`translate(${x}, ${height})`} alignmentBaseline="middle">
      <rect width={width} height={height} fill={token('colors.background.shimmer.base')} transform={transform} />
    </Group>
  );
};

function chartColor(variant: ChartVariant) {
  switch (variant) {
    case 'zero-state':
      return token('colors.background.accent.grey');
    default:
      return token('colors.background.shimmer.base');
  }
}

export const LoadingAccountProjectionChart: React.FC<Props> = ({ width, height, variant }) => {
  const xMax = Math.max(0, width);
  const yMax = Math.max(0, height - defaultMargin.top - defaultMargin.bottom);

  const performanceScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: [0, Math.max(...areaData.map(d => d.yCeiling))],
        nice: true,
      }),
    [yMax]
  );

  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [0, xMax],
        domain: [Math.min(...areaData.map(xAccessor)), Math.max(...areaData.map(xAccessor))],
      }),
    [xMax]
  );

  return (
    <svg data-testid="loading-account-projection-chart" width={width} height={height}>
      <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
      <Group top={defaultMargin.top}>
        <GridRows
          scale={performanceScale}
          width={xMax}
          height={yMax}
          strokeDasharray="4,4"
          stroke={token('colors.border.weak')}
          pointerEvents="none"
          numTicks={4}
        />

        {/* X Axis */}
        <AxisBottom
          scale={dateScale}
          top={yMax}
          tickValues={[xAccessor(areaData[0]), xAccessor(areaData[areaData.length - 1])]}
          hideTicks={true}
          axisLineClassName={css({ stroke: 'border' })}
          tickComponent={CustomTickBottom}
          tickLabelProps={(_, index) => {
            return {
              transform: index === 0 ? `translate(0, 0)` : `translate(-24, 0)`,
            };
          }}
        />
        <AreaClosed
          data={areaData}
          fill={chartColor(variant)}
          yScale={performanceScale}
          curve={curveCatmullRom}
          x={d => dateScale(xAccessor(d))}
          y0={d => performanceScale(yFloorAccessor(d))}
          y1={d => performanceScale(yCeilingAccessor(d))}
        />
        <LinePath
          data={trendLineData}
          x={d => dateScale(d.x)}
          y={d => performanceScale(d.y)}
          strokeWidth={2}
          stroke={token('colors.border')}
        />
      </Group>
    </svg>
  );
};
